import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDashboard, getConsultationAnalytics, getRevenueAnalytics } from '../api';
import { Users, Star, MessageSquare, Wallet, TrendingUp, Clock, Activity, AlertTriangle } from 'lucide-react';
import { StatCard, PageHeader, TableSkeleton } from '../components/ui';
import { formatCurrency } from '../utils/format';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const COLORS = ['#F5811F', '#7c3aed', '#10b981', '#3b82f6', '#f59e0b'];

function typeLabel(t: string) {
  return t === 'call' ? 'Audio' : t;
}

export default function DashboardPage() {
  const [revDays, setRevDays] = useState(30);

  const { data: dash, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => getDashboard().then((r) => r.data.data),
    refetchInterval: 30000,
  });

  const { data: consultAnalytics } = useQuery({
    queryKey: ['consultAnalytics', revDays],
    queryFn: () => getConsultationAnalytics(revDays).then((r) => r.data.data),
  });

  const { data: revAnalytics } = useQuery({
    queryKey: ['revAnalytics', revDays],
    queryFn: () => getRevenueAnalytics(revDays).then((r) => r.data.data),
  });

  const revenueChange = dash
    ? dash.revenue.lastMonth
      ? Math.round(((dash.revenue.thisMonth - dash.revenue.lastMonth) / dash.revenue.lastMonth) * 100)
      : 0
    : 0;

  const chartFromDash = (dash?.revenue?.chart || []).map((r: any) => ({
    date: r._id,
    amount: (r.total || 0) / 100,
  }));

  const revenueSeries = revAnalytics?.recharges?.length
    ? revAnalytics.recharges.map((r: any) => ({ date: r._id, amount: r.total / 100 }))
    : chartFromDash;

  const consultSeries = consultAnalytics?.byDay?.length
    ? consultAnalytics.byDay.map((d: any) => ({ date: d._id, count: d.count }))
    : [];

  const typeSeries = (consultAnalytics?.byType?.length
    ? consultAnalytics.byType
    : dash?.consultations?.types || []
  ).map((t: any) => ({ name: typeLabel(t._id), value: t.count }));

  return (
    <div className="p-6">
      <PageHeader
        title="Dashboard"
        subtitle="AstroVrinda Admin — real-time platform overview"
      >
        <div className="flex gap-2 text-xs">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setRevDays(d)} className={`px-3 py-1.5 rounded-lg border ${revDays === d ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
              {d}d
            </button>
          ))}
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Users" value={dash?.users?.total?.toLocaleString() || '—'} icon={<Users size={20} />} change={5} loading={isLoading} color="text-blue-500" />
        <StatCard title="Astrologers" value={dash?.astrologers?.total?.toLocaleString() || '—'} icon={<Star size={20} />} loading={isLoading} color="text-primary-500" />
        <StatCard title="Consultations" value={dash?.consultations?.total?.toLocaleString() || '—'} icon={<MessageSquare size={20} />} loading={isLoading} color="text-purple-500" />
        <StatCard title="Revenue (This Month)" value={dash ? formatCurrency(dash.revenue.thisMonth) : '—'} icon={<Wallet size={20} />} change={revenueChange} loading={isLoading} color="text-green-500" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard title="Today's Users" value={dash?.users?.today || 0} icon={<TrendingUp size={20} />} loading={isLoading} color="text-blue-400" />
        <StatCard title="Pending Approvals" value={dash?.astrologers?.pending || 0} icon={<Clock size={20} />} loading={isLoading} color="text-yellow-500" />
        <StatCard title="Active Sessions" value={dash?.consultations?.active || 0} icon={<Activity size={20} />} loading={isLoading} color="text-red-500" />
        <StatCard title="Revenue Today" value={dash ? formatCurrency(dash.revenue.today) : '—'} icon={<AlertTriangle size={20} />} loading={isLoading} color="text-indigo-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Revenue (Last {revDays} Days)</h3>
          {isLoading ? <TableSkeleton rows={4} cols={2} /> : revenueSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={revenueSeries}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F5811F" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#F5811F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => String(v).slice(5)} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v}`} />
                <Tooltip formatter={(v: any) => [`₹${v}`, 'Revenue']} />
                <Area type="monotone" dataKey="amount" stroke="#F5811F" fill="url(#revGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">No data for selected period</div>
          )}
        </div>

        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Consultations (Last {revDays} Days)</h3>
          {consultSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={consultSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => String(v).slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#F5811F" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">No data for selected period</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Consultation Types</h3>
          {typeSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={typeSeries} cx="50%" cy="50%" innerRadius={50} outerRadius={70} paddingAngle={3} dataKey="value">
                  {typeSeries.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="h-[180px] flex items-center justify-center text-gray-400 text-sm">No data</div>}
        </div>

        <div className="card p-5 col-span-1 lg:col-span-2">
          <h3 className="font-semibold text-gray-900 mb-4">Quick Overview</h3>
          <div className="space-y-3">
            {[
              { label: 'Revenue Today', value: dash ? formatCurrency(dash.revenue.today) : '—', color: 'bg-green-500' },
              { label: 'Revenue This Month', value: dash ? formatCurrency(dash.revenue.thisMonth) : '—', color: 'bg-primary-500' },
              { label: 'Online Astrologers', value: dash?.astrologers?.online ?? '—', color: 'bg-blue-500' },
              { label: 'Pending Astrologer Approvals', value: dash?.astrologers?.pending ?? '—', color: 'bg-yellow-500' },
              { label: 'New Users This Month', value: dash?.users?.thisMonth ?? '—', color: 'bg-purple-500' },
              { label: 'Consultations Today', value: dash?.consultations?.today ?? '—', color: 'bg-indigo-500' },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-2.5">
                  <div className={`w-2 h-2 rounded-full ${item.color}`} />
                  <span className="text-sm text-gray-600">{item.label}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
